import { clone, createId, imageFromPlaceholder, isImagePlaceholder, normalizeGroup } from "./ops";
import { normalizeMarks, normalizeParagraphs, scaleTextMarks } from "./richtext";
import {
  type ImageElement,
  QR_MAX_LENGTH,
  type SceneDocument,
  type SceneElement,
  type ScenePage,
  TEXT_MAX_LENGTH,
  type TextElement,
  type TextMark,
} from "./schema";

/**
 * Bulk creation ("Serienerstellung", mail merge): one copy of a template page
 * per row of a table, with `{{Spalte}}` placeholders in texts and QR codes
 * filled in and elements mapped to columns replaced.
 *
 * Everything here is pure: the editor applies `insertMergedPages` inside one
 * `update` (one undo step, one set of live ops); files are built with
 * `buildMergedDocument` and created through the regular documents API.
 */

/** A document holds at most 100 pages (`sceneDocumentSchema`). */
export const MERGE_MAX_PAGES = 100;
/** Files created in one go (each one is a request). */
export const MERGE_MAX_FILES = 100;
/** Rows kept from an imported table. */
export const MERGE_MAX_ROWS = 1000;

// ---------------------------------------------------------------------------
// Tables (CSV / TSV / pasted from a spreadsheet)
// ---------------------------------------------------------------------------

export interface DataTable {
  headers: string[];
  rows: string[][];
}

export type Separator = "," | ";" | "\t";

/**
 * The separator of a CSV text: the candidate that splits the first lines
 * into the same, largest number of fields (quoted parts ignored). Tabs win
 * ties (pasted spreadsheet cells), then semicolons (German Excel).
 */
export function detectSeparator(text: string): Separator {
  const lines = stripBom(text)
    .split(/\r\n|\n|\r/)
    .filter((line) => line.trim() !== "")
    .slice(0, 10)
    .map((line) => line.replace(/"(?:[^"]|"")*"/g, ""));
  if (lines.length === 0) return ",";
  let best: Separator = ",";
  let bestScore = 0;
  for (const separator of ["\t", ";", ","] as const) {
    const counts = lines.map((line) => line.split(separator).length - 1);
    const first = counts[0]!;
    if (first === 0) continue;
    const consistent = counts.filter((count) => count === first).length / counts.length;
    const score = first * consistent;
    if (score > bestScore) {
      best = separator;
      bestScore = score;
    }
  }
  return best;
}

function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

/** Splits CSV text into records (RFC 4180: quotes, doubled quotes, line breaks in quotes). */
export function parseCsv(text: string, separator: Separator = detectSeparator(text)): string[][] {
  const source = stripBom(text);
  const records: string[][] = [];
  let record: string[] = [];
  let field = "";
  let quoted = false;
  let fieldStarted = false;
  for (let index = 0; index < source.length; index++) {
    const char = source[index]!;
    if (quoted) {
      if (char === '"') {
        if (source[index + 1] === '"') {
          field += '"';
          index++;
        } else {
          quoted = false;
        }
      } else {
        field += char;
      }
      continue;
    }
    if (char === '"' && !fieldStarted) {
      quoted = true;
      fieldStarted = true;
    } else if (char === separator) {
      record.push(field);
      field = "";
      fieldStarted = false;
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && source[index + 1] === "\n") index++;
      record.push(field);
      records.push(record);
      record = [];
      field = "";
      fieldStarted = false;
    } else {
      field += char;
      if (char !== " ") fieldStarted = true;
    }
  }
  if (field !== "" || record.length > 0) {
    record.push(field);
    records.push(record);
  }
  return records.filter((row) => row.some((cell) => cell.trim() !== ""));
}

/**
 * A table with headers from CSV/TSV text. Headers are trimmed; empty ones
 * become "Spalte N", repeated ones get a number. Rows are padded to the
 * header width; cells beyond it are dropped.
 */
export function parseTable(text: string, separator?: Separator): DataTable {
  const records = parseCsv(text, separator ?? detectSeparator(text));
  const [head, ...body] = records;
  if (!head) return { headers: [], rows: [] };
  const width = Math.max(head.length, ...body.map((row) => row.length));
  const headers = uniqueHeaders(Array.from({ length: width }, (_, index) => head[index] ?? ""));
  const rows = body
    .slice(0, MERGE_MAX_ROWS)
    .map((row) => headers.map((_, index) => (row[index] ?? "").replace(/\r\n?/g, "\n")));
  return { headers, rows };
}

export function uniqueHeaders(raw: readonly string[]): string[] {
  const seen = new Set<string>();
  return raw.map((header, index) => {
    let name = header.trim().replace(/\s+/g, " ") || `Spalte ${index + 1}`;
    if (seen.has(columnKey(name))) {
      let suffix = 2;
      while (seen.has(columnKey(`${name} ${suffix}`))) suffix++;
      name = `${name} ${suffix}`;
    }
    seen.add(columnKey(name));
    return name;
  });
}

/** Column names match case-insensitively and ignoring surrounding/double spaces. */
export function columnKey(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLocaleLowerCase("de");
}

/** The rows as records keyed by header. */
export function tableRecords(table: DataTable): Record<string, string>[] {
  return table.rows.map((row) =>
    Object.fromEntries(table.headers.map((header, index) => [header, row[index] ?? ""])),
  );
}

/** CSV text of a table (semicolons, like German Excel), e.g. to download the grid. */
export function formatCsv(table: DataTable, separator: Separator = ";"): string {
  const cell = (value: string) =>
    /["\n\r]/.test(value) || value.includes(separator) ? `"${value.replace(/"/g, '""')}"` : value;
  return [table.headers, ...table.rows].map((row) => row.map(cell).join(separator)).join("\n");
}

// ---------------------------------------------------------------------------
// Placeholders
// ---------------------------------------------------------------------------

export interface Placeholder {
  from: number;
  to: number;
  /** Name as written between the braces, trimmed. */
  name: string;
}

const PLACEHOLDER = /\{\{([^{}\n]+?)\}\}/g;

export function findPlaceholders(text: string): Placeholder[] {
  const out: Placeholder[] = [];
  for (const match of text.matchAll(PLACEHOLDER)) {
    const name = match[1]!.trim();
    if (!name) continue;
    out.push({ from: match.index, to: match.index + match[0].length, name });
  }
  return out;
}

/** Looks up a value by placeholder or column name (see `columnKey`). */
export type ValueLookup = (name: string) => string | undefined;

export function recordLookup(record: Record<string, string>): ValueLookup {
  const byKey = new Map(Object.entries(record).map(([key, value]) => [columnKey(key), value]));
  return (name) => byKey.get(columnKey(name));
}

/** Fills placeholders of a plain string; unknown ones stay as they are. */
export function fillString(text: string, lookup: ValueLookup): string {
  return text.replace(PLACEHOLDER, (whole, name: string) => {
    const value = lookup(name.trim());
    return value === undefined ? whole : value;
  });
}

interface Replacement {
  from: number;
  to: number;
  value: string;
}

/**
 * Replaces ranges of a text element's text, keeping its rich text in place:
 * marks before and after move with the text, a mark covering (part of) a
 * replaced range covers the whole new value – so a bold `{{Name}}` becomes a
 * bold name. Line breaks in a value repeat the paragraph format (list items).
 */
export function fillTextRanges(element: TextElement, replacements: readonly Replacement[]): void {
  const sorted = [...replacements]
    .filter((item) => item.to > item.from || item.value !== "")
    .sort((a, b) => b.from - a.from);
  if (sorted.length === 0) return;
  let text = element.text;
  let marks: TextMark[] | undefined = element.marks ? clone(element.marks) : undefined;
  const paragraphs = element.paragraphs ? [...element.paragraphs] : undefined;
  let previousFrom = Number.POSITIVE_INFINITY;
  for (const { from, to, value: raw } of sorted) {
    if (to > previousFrom) continue; // overlapping ranges: keep the later one
    previousFrom = from;
    const value = raw.replace(/\r\n?/g, "\n");
    const delta = value.length - (to - from);
    const mapStart = (position: number) =>
      position <= from ? position : position >= to ? position + delta : from;
    const mapEnd = (position: number) =>
      position <= from ? position : position >= to ? position + delta : from + value.length;
    if (marks) {
      marks = marks.map((mark) => ({ ...mark, from: mapStart(mark.from), to: mapEnd(mark.to) }));
    }
    if (paragraphs) {
      const index = text.slice(0, from).split("\n").length - 1;
      const removed = text.slice(from, to).split("\n").length - 1;
      const added = value.split("\n").length - 1;
      const format = paragraphs[index] ?? null;
      paragraphs.splice(index + 1, removed, ...Array.from({ length: added }, () => format));
    }
    text = text.slice(0, from) + value + text.slice(to);
  }
  if (text.length > TEXT_MAX_LENGTH) text = text.slice(0, TEXT_MAX_LENGTH);
  element.text = text;
  const normalizedMarks = normalizeMarks(marks, text.length);
  if (normalizedMarks.length > 0) element.marks = normalizedMarks;
  else delete element.marks;
  const normalizedParagraphs = normalizeParagraphs(paragraphs, text.split("\n").length);
  if (normalizedParagraphs) element.paragraphs = normalizedParagraphs;
  else delete element.paragraphs;
}

/** Fills the placeholders of a text element (formatting kept, see `fillTextRanges`). */
export function fillTextElement(element: TextElement, lookup: ValueLookup): boolean {
  const replacements: Replacement[] = [];
  for (const placeholder of findPlaceholders(element.text)) {
    const value = lookup(placeholder.name);
    if (value !== undefined) replacements.push({ ...placeholder, value });
  }
  fillTextRanges(element, replacements);
  return replacements.length > 0;
}

/** Ids of a page's elements, group contents included, in drawing order. */
export function pageElementIds(doc: SceneDocument, pageIndex: number): string[] {
  const out: string[] = [];
  const visit = (id: string, depth: number) => {
    const element = doc.elements[id];
    if (!element || depth > 50) return;
    out.push(id);
    if (element.type === "group") for (const child of element.children) visit(child, depth + 1);
  };
  for (const id of doc.pages[pageIndex]?.children ?? []) visit(id, 0);
  return out;
}

export interface ElementPlaceholders {
  elementId: string;
  kind: "text" | "qr";
  /** Distinct names in order of appearance. */
  names: string[];
}

/** Placeholders in the texts and QR codes of a page. */
export function pagePlaceholders(doc: SceneDocument, pageIndex: number): ElementPlaceholders[] {
  const out: ElementPlaceholders[] = [];
  for (const id of pageElementIds(doc, pageIndex)) {
    const element = doc.elements[id]!;
    const source =
      element.type === "text" ? element.text : element.type === "qr" ? element.value : null;
    if (source === null) continue;
    const names = [...new Set(findPlaceholders(source).map((item) => item.name))];
    if (names.length > 0) out.push({ elementId: id, kind: element.type as "text" | "qr", names });
  }
  return out;
}

/** Distinct placeholder names of a page (first spelling wins). */
export function placeholderNames(doc: SceneDocument, pageIndex: number): string[] {
  const byKey = new Map<string, string>();
  for (const entry of pagePlaceholders(doc, pageIndex)) {
    for (const name of entry.names)
      if (!byKey.has(columnKey(name))) byKey.set(columnKey(name), name);
  }
  return [...byKey.values()];
}

// ---------------------------------------------------------------------------
// Mappings and merging
// ---------------------------------------------------------------------------

/**
 * Manual mapping of an element to a column: texts get the value as their
 * whole text (formatting of the first character kept), QR codes as value,
 * images and image placeholders the media asset named in the column.
 */
export interface MergeMapping {
  elementId: string;
  column: string;
}

export interface MergeOptions {
  mappings?: readonly MergeMapping[];
  /** Asset id for a cell value of an image column (file name → media library). */
  resolveImage?: (value: string) => string | undefined;
  /** Page name pattern, e.g. "Gutschein {{Name}}". */
  pageName?: string;
}

/** Elements that can be mapped to a column manually. */
export function isMappable(element: SceneElement | undefined): boolean {
  return (
    element?.type === "text" ||
    element?.type === "qr" ||
    element?.type === "image" ||
    isImagePlaceholder(element)
  );
}

export interface ColumnUsage {
  /** Columns neither used by a placeholder nor by a mapping. */
  unused: string[];
  /** Placeholder names without a column (they stay as written). */
  missing: string[];
}

export function columnUsage(
  headers: readonly string[],
  placeholders: readonly string[],
  mappings: readonly MergeMapping[] = [],
): ColumnUsage {
  const columns = new Set(headers.map(columnKey));
  const used = new Set(
    [...placeholders, ...mappings.map((mapping) => mapping.column)].map(columnKey),
  );
  return {
    unused: headers.filter((header) => !used.has(columnKey(header))),
    missing: placeholders.filter((name) => !columns.has(columnKey(name))),
  };
}

function fillElement(
  element: SceneElement,
  lookup: ValueLookup,
  mapped: string | undefined,
  options: MergeOptions,
): SceneElement {
  if (element.type === "text") {
    if (mapped !== undefined) {
      fillTextRanges(element, [{ from: 0, to: element.text.length, value: mapped }]);
    } else {
      fillTextElement(element, lookup);
    }
    return element;
  }
  if (element.type === "qr") {
    const value = (mapped ?? fillString(element.value, lookup)).slice(0, QR_MAX_LENGTH);
    // An empty cell would make an invalid QR code; the template value stays.
    if (value.trim() !== "") element.value = value;
    return element;
  }
  if (mapped !== undefined && mapped.trim() !== "" && options.resolveImage) {
    const assetId = options.resolveImage(mapped.trim());
    if (!assetId) return element;
    if (isImagePlaceholder(element)) return imageFromPlaceholder(element, assetId);
    if (element.type === "image" && element.assetId !== assetId) {
      const image: ImageElement = element;
      image.assetId = assetId;
      // The crop belongs to the old picture; "cover" fills the box with the new one.
      image.crop = null;
      image.fit = "cover";
    }
  }
  return element;
}

export interface MergedPage {
  page: ScenePage;
  /** New elements of the page, keyed by their fresh ids. */
  elements: Record<string, SceneElement>;
  /** Template element id → id on the new page. */
  idMap: Map<string, string>;
}

/** A filled copy of a template page with fresh ids (the document is not changed). */
export function mergePage(
  doc: SceneDocument,
  pageIndex: number,
  record: Record<string, string>,
  options: MergeOptions = {},
): MergedPage {
  const template = doc.pages[pageIndex];
  if (!template) throw new Error(`Seite ${pageIndex + 1} existiert nicht`);
  const lookup = recordLookup(record);
  const mappedColumn = new Map(
    (options.mappings ?? []).map((mapping) => [mapping.elementId, mapping.column]),
  );
  const ids = pageElementIds(doc, pageIndex);
  const idMap = new Map(ids.map((id) => [id, createId(id.split("_")[0] || "el")]));
  const elements: Record<string, SceneElement> = {};
  for (const oldId of ids) {
    let copy = clone(doc.elements[oldId]!);
    const column = mappedColumn.get(oldId);
    copy = fillElement(copy, lookup, column === undefined ? undefined : lookup(column), options);
    copy.id = idMap.get(oldId)!;
    if (copy.type === "group") copy.children = copy.children.map((child) => idMap.get(child)!);
    elements[copy.id] = copy;
  }
  const name = options.pageName ? fillString(options.pageName, lookup).trim().slice(0, 100) : "";
  const page: ScenePage = {
    id: createId("page"),
    background: clone(template.background),
    children: template.children.map((child) => idMap.get(child)!).filter(Boolean),
    ...(template.brand ? { brand: clone(template.brand) } : {}),
    ...(name ? { name } : template.name ? { name: template.name } : {}),
  };
  return { page, elements, idMap };
}

/** Removes a page and its elements (also the last one; callers add pages back). */
function dropPage(doc: SceneDocument, pageIndex: number): void {
  for (const id of pageElementIds(doc, pageIndex)) delete doc.elements[id];
  doc.pages.splice(pageIndex, 1);
}

/** Rows that fit into the document (see `MERGE_MAX_PAGES`). */
export function mergePageCapacity(doc: SceneDocument, replace: boolean): number {
  return Math.max(0, MERGE_MAX_PAGES - doc.pages.length + (replace ? 1 : 0));
}

/**
 * Inserts one filled copy of the template page per record, after the
 * template or in its place. Returns the index of the first new page and the
 * new pages' element id maps. Throws when the pages do not fit.
 */
export function insertMergedPages(
  doc: SceneDocument,
  pageIndex: number,
  records: readonly Record<string, string>[],
  options: MergeOptions & { replace?: boolean } = {},
): { first: number; pages: MergedPage[] } {
  if (!doc.pages[pageIndex]) throw new Error(`Seite ${pageIndex + 1} existiert nicht`);
  if (records.length === 0) throw new Error("Keine Zeilen");
  if (records.length > mergePageCapacity(doc, Boolean(options.replace))) {
    throw new Error(`Ein Dokument hat höchstens ${MERGE_MAX_PAGES} Seiten`);
  }
  const pages = records.map((record) => mergePage(doc, pageIndex, record, options));
  const first = applyMergedPages(doc, pageIndex, pages, Boolean(options.replace));
  return { first, pages };
}

/**
 * Inserts pages built with `mergePage` (e.g. after measuring their texts)
 * after the template page or in its place. Returns the first new index.
 */
export function applyMergedPages(
  doc: SceneDocument,
  pageIndex: number,
  pages: readonly MergedPage[],
  replace: boolean,
): number {
  if (!doc.pages[pageIndex]) throw new Error(`Seite ${pageIndex + 1} existiert nicht`);
  if (pages.length === 0 || pages.length > mergePageCapacity(doc, replace)) {
    throw new Error(`Ein Dokument hat höchstens ${MERGE_MAX_PAGES} Seiten`);
  }
  for (const merged of pages) Object.assign(doc.elements, clone(merged.elements));
  const first = replace ? pageIndex : pageIndex + 1;
  if (replace) dropPage(doc, pageIndex);
  doc.pages.splice(first, 0, ...pages.map((merged) => clone(merged.page)));
  normalizeMergedGroups(doc, pages);
  return first;
}

/** Group boxes follow their children again after texts changed their height. */
export function normalizeMergedGroups(doc: SceneDocument, pages: readonly MergedPage[]): void {
  for (const merged of pages) {
    // Children come after their groups in `idMap` order: innermost groups first.
    for (const id of [...merged.idMap.values()].reverse()) {
      if (doc.elements[id]?.type === "group") normalizeGroup(doc, id);
    }
  }
}

/** A single-page document from the filled template page (one file per row). */
export function buildMergedDocument(
  doc: SceneDocument,
  pageIndex: number,
  record: Record<string, string>,
  options: MergeOptions = {},
): { doc: SceneDocument; merged: MergedPage } {
  const merged = mergePage(doc, pageIndex, record, options);
  const { pages: _pages, elements: _elements, timeline: _timeline, ...rest } = doc;
  return {
    doc: { ...clone(rest), pages: [merged.page], elements: merged.elements },
    merged,
  };
}

/**
 * File title of a row from a pattern like "Gutschein {{Name}}". Falls back to
 * "<fallback> <n>" when the pattern yields nothing; at most 200 characters.
 */
export function mergeTitle(
  pattern: string,
  record: Record<string, string>,
  index: number,
  fallback: string,
): string {
  const title = fillString(pattern, recordLookup(record)).replace(/\s+/g, " ").trim();
  return (title || `${fallback} ${index + 1}`).slice(0, 200);
}

// ---------------------------------------------------------------------------
// Fitting long values
// ---------------------------------------------------------------------------

/**
 * Scales a text's font sizes (base style, marks and the size relative to a
 * linked brand text style), e.g. to shrink a long value into its box.
 */
export function scaleTextSize(element: TextElement, factor: number): void {
  if (!(factor > 0) || factor === 1) return;
  element.style.fontSize = Math.max(1, Math.round(element.style.fontSize * factor * 100) / 100);
  scaleTextMarks(element, factor);
  if (element.textScale !== undefined) {
    element.textScale = Math.min(10, Math.max(0.1, element.textScale * factor));
  }
}

/**
 * Finds per-text scale factors so that every text fits its maximum height:
 * a bisection over `measure` (heights of the texts at the given factors),
 * which may render all texts at once. Texts that fit get factor 1.
 */
export async function fitTextScales(
  targets: readonly { id: string; maxHeight: number }[],
  measure: (
    factors: ReadonlyMap<string, number>,
  ) => Promise<Map<string, number>> | Map<string, number>,
  options: { minFactor?: number; steps?: number } = {},
): Promise<Map<string, number>> {
  const minFactor = options.minFactor ?? 0.3;
  const steps = options.steps ?? 7;
  const result = new Map<string, number>(targets.map((target) => [target.id, 1]));
  const full = await measure(result);
  const open = targets.filter((target) => (full.get(target.id) ?? 0) > target.maxHeight + 0.5);
  const low = new Map(open.map((target) => [target.id, minFactor]));
  const high = new Map(open.map((target) => [target.id, 1]));
  for (const target of open) result.set(target.id, minFactor);
  for (let step = 0; step < steps && open.length > 0; step++) {
    const probe = new Map(
      open.map((target) => [target.id, (low.get(target.id)! + high.get(target.id)!) / 2]),
    );
    const heights = await measure(probe);
    for (const target of open) {
      const factor = probe.get(target.id)!;
      if ((heights.get(target.id) ?? 0) <= target.maxHeight + 0.5) {
        low.set(target.id, factor);
        result.set(target.id, factor);
      } else {
        high.set(target.id, factor);
      }
    }
  }
  return result;
}
